import {
  Federation,
  ModuleFederationRuntimePlugin,
  registerGlobalPlugins,
  ModuleFederation,
} from '@module-federation/enhanced/runtime';
import { types as moduleFederationTypes } from '@module-federation/runtime-core';

const OVERRIDE_EXO_SHARED_REACT_PLUGIN_NAME = 'exo-shared-react-override';
const SHARED_REACT_PACKAGES = ['react', 'react-dom'];
const DEFAULT_SCOPE = 'default';

/**
 * Dual shape returned by the resolvers: MF 0.x reads the `Shared` fields off
 * the top level (spread), MF 2.x reads the nested `.shared` + `.useTreesShaking`.
 * `satisfies` this at every return site so a dropped field fails to compile.
 */
type DualShapeShared = moduleFederationTypes.Shared & {
  shared: moduleFederationTypes.Shared;
  useTreesShaking: boolean;
};

export interface RequiredHostVersions {
  react: string;
  'react-dom': string;
}

export interface ReactOverridePluginOptions {
  /**
   * Share scope names that contain versioned React instances.
   * When a remote requests react/react-dom from one of these scopes,
   * the plugin resolves from shareScopeMap[scope] instead of
   * shareScopeMap.default, letting the native versioned resolution proceed.
   * e.g. ['react17', 'react18', 'react19']
   */
  availableReactScopes?: string[];
}

type availableReactScopesSet = Set<string>;

function buildAvailableReactScopesSet(
  options: ReactOverridePluginOptions | undefined,
): availableReactScopesSet | undefined {
  return options?.availableReactScopes
    ? new Set(options.availableReactScopes.filter((s) => s !== DEFAULT_SCOPE))
    : undefined;
}

const isSharedReactPackage = (packageName: string) =>
  SHARED_REACT_PACKAGES.includes(packageName);

/**
 * Finds the best matching shared configuration for a given package and version pattern
 * First tries exact pattern match, then looks for configs with matching shareConfig.requiredVersion
 */
export function findBestMatchingSharedConfig(
  hostSharedConfigByVersion: Record<string, moduleFederationTypes.Shared>,
  requiredHostVersions: RequiredHostVersions,
  hostName: string,
  packageName: string,
  shouldLog = false,
): moduleFederationTypes.Shared | undefined {
  const requiredHostVersionPattern =
    requiredHostVersions[packageName as keyof RequiredHostVersions];
  // Step 1: Try exact pattern match (e.g., '17.0.2')
  const exactMatch = hostSharedConfigByVersion?.[requiredHostVersionPattern];
  if (exactMatch && exactMatch.from === hostName) {
    return exactMatch;
  }

  // Step 2: Find host configs where shareConfig.requiredVersion matches the pattern
  const hostConfigs = Object.values(hostSharedConfigByVersion).filter(
    (config) => config.from === hostName,
  );
  const hostMatchingConfig = hostConfigs.find(
    (config) =>
      config.shareConfig?.requiredVersion === requiredHostVersionPattern,
  );

  if (hostMatchingConfig) {
    return hostMatchingConfig;
  }

  // step 3: find other matching configs for requiredHostVersionPattern but from a different instance
  // in @module-federation/runtime-core > 0.21 there is no guarantee that the from will hold the host name
  const otherMatchingConfigs = Object.values(hostSharedConfigByVersion).filter(
    (config) =>
      config.from !== hostName &&
      config.shareConfig.requiredVersion === requiredHostVersionPattern,
  );
  if (otherMatchingConfigs.length > 0) {
    return otherMatchingConfigs[0];
  }

  if (shouldLog) {
    console.error(
      `[ExoSkeleton][ExoSharedReactOverridePlugin] Host "${hostName}" shared config for "${packageName}" version pattern "${requiredHostVersionPattern}" not found. Trying fallback to any host config.`,
    );
  }

  // Step 4: Fallback to any host config if no pattern match found
  if (shouldLog && hostConfigs.length === 0) {
    console.error(
      `[ExoSkeleton][ExoSharedReactOverridePlugin] Host "${hostName}" shared config for "${packageName}" version pattern "${requiredHostVersionPattern}" not found. This may cause issues with React version consistency across remotes.`,
    );
    return undefined;
  }

  if (shouldLog && hostConfigs.length > 1) {
    console.error(
      `[ExoSkeleton][ExoSharedReactOverridePlugin] Host "${hostName}" shared config for "${packageName}" has multiple versions. This may cause issues with React version consistency across remotes.`,
    );
  }

  return hostConfigs[0];
}

export function findHostInstance(
  globalFederation: Federation,
  hostName: string,
): ModuleFederation | undefined {
  const instances = globalFederation?.__INSTANCES__;
  if (!instances || !Array.isArray(instances) || instances.length === 0) {
    return undefined;
  }

  return instances.find(
    (instance: ModuleFederation) =>
      instance?.options?.name === hostName || instance?.name === hostName,
  );
}

/**
 * Validates the host's React shared configuration and log errors/warnings about potential miss-configurations
 */
function validateScopeShareConfig(
  hostName: string,
  scopeName: string,
  scopeConfig: Record<string, moduleFederationTypes.Shared> | undefined,
  packageName: string,
): void {
  if (!scopeConfig || Object.keys(scopeConfig).length === 0) {
    console.error(
      `[ExoSkeleton][ExoSharedReactOverridePlugin] Host "${hostName}" shared config for "${packageName}" in scope "${scopeName}" not found. Remotes requesting this scope will not receive the correct React version.`,
    );
    return;
  }

  const firstConfig = Object.values(scopeConfig)[0];
  const shareConfig = firstConfig?.shareConfig;

  if (!shareConfig) {
    console.error(
      `[ExoSkeleton][ExoSharedReactOverridePlugin] Host "${hostName}" shared config for "${packageName}" in scope "${scopeName}" is missing shareConfig. This may cause issues with React version consistency across remotes.`,
    );
    return;
  }

  if (!shareConfig.singleton) {
    console.error(
      `[ExoSkeleton][ExoSharedReactOverridePlugin] Host "${hostName}" shared config for "${packageName}" in scope "${scopeName}" is missing singleton: true. This may cause issues with React version consistency across remotes.`,
    );
  }

  if (!shareConfig.eager) {
    console.error(
      `[ExoSkeleton][ExoSharedReactOverridePlugin] Host "${hostName}" shared config for "${packageName}" in scope "${scopeName}" is missing eager: true. This may cause issues with React version consistency across remotes.`,
    );
  }

  if (!shareConfig.requiredVersion) {
    console.error(
      `[ExoSkeleton][ExoSharedReactOverridePlugin] Host "${hostName}" shared config for "${packageName}" in scope "${scopeName}" is missing requiredVersion: "<version>". This may cause issues with React version consistency across remotes.`,
    );
  }
}

export function validateHostReactConfig(
  hostName: string,
  requiredHostVersions: RequiredHostVersions,
): void {
  try {
    const globalFederation: Federation = globalThis.__FEDERATION__;
    if (!globalFederation?.__INSTANCES__) {
      console.error(
        `[ExoSkeleton][ExoSharedReactOverridePlugin] Could not validate host React config: No federation instances found`,
      );
      return;
    }

    const host = findHostInstance(globalFederation, hostName);
    if (!host) {
      console.error(
        `[ExoSkeleton][ExoSharedReactOverridePlugin] Could not validate host React config: Host "${hostName}" not found`,
      );
      return;
    }

    SHARED_REACT_PACKAGES.forEach((packageName) => {
      const hostSharedConfigByVersion =
        host?.shareScopeMap?.[DEFAULT_SCOPE]?.[packageName];
      if (!hostSharedConfigByVersion) {
        console.error(
          `[ExoSkeleton][ExoSharedReactOverridePlugin] Host "${hostName}" shared config for "${packageName}" not found. This may cause issues with React version consistency across remotes.`,
        );
        return;
      }

      const selectedHostSharedConfig = findBestMatchingSharedConfig(
        hostSharedConfigByVersion,
        requiredHostVersions,
        hostName,
        packageName,
        true,
      );

      if (!selectedHostSharedConfig) {
        return;
      }

      validateScopeShareConfig(
        hostName,
        DEFAULT_SCOPE,
        {
          [selectedHostSharedConfig.version ?? DEFAULT_SCOPE]:
            selectedHostSharedConfig,
        },
        packageName,
      );
    });
  } catch (error) {
    console.error(
      `[ExoSkeleton][ExoSharedReactOverridePlugin] Error validating host React config for "${hostName}":`,
      error,
    );
  }
}

export function isPluginAlreadyRegistered(pluginName: string): boolean {
  const globalPlugins = globalThis?.__FEDERATION__?.__GLOBAL_PLUGIN__;
  if (!globalPlugins || !Array.isArray(globalPlugins)) return false;

  return globalPlugins.some(
    (plugin: ModuleFederationRuntimePlugin) => plugin?.name === pluginName,
  );
}

/**
 * Handles resolving shared React packages to use the host's version.
 * For versioned scopes (react17, react18, react19), sets the resolver to read
 * directly from shareScopeMap[scope][pkgName][version] — ensuring the MF
 * runtime picks up the seeded entry rather than falling back to the default
 * resolver. For the default scope, uses findBestMatchingSharedConfig to match
 * requiredHostVersions.
 */
function createResolveShareHandler(
  hostName: string,
  requiredHostVersions: RequiredHostVersions,
  availableReactScopes?: availableReactScopesSet,
): NonNullable<ModuleFederationRuntimePlugin['resolveShare']> {
  return (args) => {
    try {
      if (isSharedReactPackage(args?.pkgName)) {
        if (availableReactScopes?.has(args.scope)) {
          const { shareScopeMap, scope, pkgName, version } = args;
          args.resolver = () => {
            const entry = shareScopeMap?.[scope]?.[pkgName]?.[version];
            if (!entry) {
              console.warn(
                `[ExoSkeleton][ExoSharedReactOverridePlugin] Version key "${version}" not found in scope "${scope}" for "${pkgName}". The remote may receive an isolated React copy.`,
              );
              return undefined;
            }
            const eagerShared = { ...entry, eager: true };
            // Dual shape: MF 0.x reads the Shared fields off the top level
            // (spread), MF 2.x reads the nested `.shared` + `.useTreesShaking`.
            // A single global resolver must satisfy both in a mixed federation.
            return {
              ...eagerShared,
              // Declared after the spread so `...eagerShared` can't clobber it.
              shared: { ...eagerShared },
              // MF 2.x tree-shaking flag — note the upstream misspelling 'Trees'.
              useTreesShaking: false,
            } satisfies DualShapeShared;
          };
          return args;
        }

        args.resolver = () => {
          try {
            const host = findHostInstance(args.GlobalFederation, hostName);
            if (!host) {
              return undefined;
            }

            const hostSharedConfigByVersion =
              host?.shareScopeMap?.default?.[args.pkgName];

            if (!hostSharedConfigByVersion) {
              return undefined;
            }

            const selectedHostSharedConfig = findBestMatchingSharedConfig(
              hostSharedConfigByVersion,
              requiredHostVersions,
              hostName,
              args.pkgName,
              false,
            );

            if (!selectedHostSharedConfig) {
              return undefined;
            }

            const eagerShared = {
              ...selectedHostSharedConfig,
              eager: true,
            } satisfies moduleFederationTypes.Shared;
            // Dual shape: MF 0.x reads the Shared fields off the top level
            // (spread), MF 2.x reads the nested `.shared` + `.useTreesShaking`.
            // A single global resolver must satisfy both in a mixed federation.
            return {
              ...eagerShared,
              // Declared after the spread so `...eagerShared` can't clobber it.
              shared: { ...eagerShared },
              // MF 2.x tree-shaking flag — note the upstream misspelling 'Trees'.
              useTreesShaking: false,
            } satisfies DualShapeShared;
          } catch (error) {
            console.error(
              `[ExoSkeleton][ExoSharedReactOverridePlugin] Error in resolver for "${args.pkgName}":`,
              error,
            );
            return undefined;
          }
        };
      }

      return args;
    } catch (error) {
      console.error(
        `[ExoSkeleton][ExoSharedReactOverridePlugin] Error in resolveShare for "${args?.pkgName}":`,
        error,
      );
      return args;
    }
  };
}

function resolveRequestedScope(
  args: Parameters<
    NonNullable<ModuleFederationRuntimePlugin['beforeLoadShare']>
  >[0],
  availableReactScopes: availableReactScopesSet | undefined,
): { scope: string; isVersionedScope: boolean } {
  const requestedScope = Array.isArray(args.shareInfo?.scope)
    ? args.shareInfo.scope[0]
    : args.shareInfo?.scope;

  const isVersionedScope =
    requestedScope !== null &&
    requestedScope !== undefined &&
    (availableReactScopes?.has(requestedScope) ?? false);

  return {
    scope: isVersionedScope ? requestedScope : DEFAULT_SCOPE,
    isVersionedScope,
  };
}

/**
 * Handles forcing shared React packages to use the host's version before loading.
 * For the default scope, uses findBestMatchingSharedConfig. For versioned scopes,
 * resolves from shareScopeMap[scope] directly using Object.values()[0] — consistent
 * with the assumption that each versioned scope contains exactly one React version.
 */
function createBeforeLoadShareHandler(
  hostName: string,
  requiredHostVersions: RequiredHostVersions,
  availableReactScopes?: availableReactScopesSet,
): NonNullable<ModuleFederationRuntimePlugin['beforeLoadShare']> {
  return (args) => {
    try {
      if (isSharedReactPackage(args?.pkgName)) {
        const { scope, isVersionedScope } = resolveRequestedScope(
          args,
          availableReactScopes,
        );
        const reactScope = args?.origin?.shareScopeMap?.[scope]?.[args.pkgName];

        if (reactScope) {
          const selectedHostSharedConfig = isVersionedScope
            ? Object.values(reactScope)[0]
            : findBestMatchingSharedConfig(
                reactScope,
                requiredHostVersions,
                hostName,
                args.pkgName,
                false,
              );

          if (!selectedHostSharedConfig) {
            return args;
          }

          args.shareInfo = {
            ...selectedHostSharedConfig,
            eager: true,
          };
        }
      }

      return args;
    } catch (error) {
      console.error(
        `[ExoSkeleton][ExoSharedReactOverridePlugin] Error in beforeLoadShare for "${args?.pkgName}":`,
        error,
      );
      return args;
    }
  };
}

/**
 * Creates a runtime plugin that forces remotes to use the host's React version
 * instead of their own React versions to prevent multiple React instances
 */
export function reactVersionOverridePlugin(
  hostName: string,
  requiredHostVersions: RequiredHostVersions,
  options?: ReactOverridePluginOptions,
): ModuleFederationRuntimePlugin {
  const availableReactScopes = buildAvailableReactScopesSet(options);
  return {
    name: OVERRIDE_EXO_SHARED_REACT_PLUGIN_NAME,
    resolveShare: createResolveShareHandler(
      hostName,
      requiredHostVersions,
      availableReactScopes,
    ),
    beforeLoadShare: createBeforeLoadShareHandler(
      hostName,
      requiredHostVersions,
      availableReactScopes,
    ),
  };
}

export function registerReactOverrideRuntimePlugin(
  hostName: string,
  requiredHostVersions: RequiredHostVersions,
  options?: ReactOverridePluginOptions,
): void {
  try {
    if (isPluginAlreadyRegistered(OVERRIDE_EXO_SHARED_REACT_PLUGIN_NAME)) {
      return;
    }

    const plugin = reactVersionOverridePlugin(
      hostName,
      requiredHostVersions,
      options,
    );
    validateHostReactConfig(hostName, requiredHostVersions);

    registerGlobalPlugins([plugin]);
  } catch (error) {
    console.error(
      `[ExoSkeleton][ExoSharedReactOverridePlugin] Error registering React override plugin for host "${hostName}":`,
      error,
    );
  }
}
